import axios from "axios";
import { appPlatform } from "@/lib/platform";
import { guardOn } from "@/lib/guards";
import { isOffline } from "@/lib/netStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;
const TOKEN_KEY = "yalola.token";

const DEVICE_KEY = "yalola.device_id";

// Lot 713 — résilience réseau (garde-fou « net_resilience ») : délai de lecture adapté au lien (2G ×1,6 · 3G ×1,25) et
// lectures (GET) réessayées 2 fois (0,7 s puis 1,8 s, + aléa) quand le serveur n'a PAS répondu (réseau coupé un instant,
// délai dépassé). Jamais sur une réponse serveur (4xx/5xx), une requête annulée, un appareil hors ligne, ni /auth/me
// (qui a sa propre boucle de reconnexion).
const RETRY_DELAYS = [700, 1800];
export const adaptiveTimeout = (base = 30000) => {
  const et = typeof navigator !== "undefined" ? navigator.connection?.effectiveType || "" : "";
  if (/(^|-)2g$/.test(et)) return Math.round(base * 1.6);
  if (et === "3g") return Math.round(base * 1.25);
  return base;
};
export const retryDelay = (n) => (n < RETRY_DELAYS.length ? RETRY_DELAYS[n] + Math.floor(Math.random() * 300) : null);
export const shouldRetryRead = (err, cfg) => {
  if (!cfg || String(cfg.method || "get").toLowerCase() !== "get" || cfg.__noRetry) return false;
  if (/\/auth\/me(\?|$)/.test(String(cfg.url || ""))) return false;
  if (err?.response || axios.isCancel(err) || err?.code === "ERR_CANCELED") return false;
  if ((cfg.__retries || 0) >= RETRY_DELAYS.length) return false;
  return !isOffline();
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

// Persistent per-browser device identifier used for the "trusted device" security flow.
export const getDeviceId = () => {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) {
    d = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, d);
  }
  return d;
};

export const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  config.headers["x-device-id"] = getDeviceId();
  // web | android | ios → the server applies the platform payment rules (Stripe / stores / iOS gifts).
  config.headers["x-platform"] = appPlatform();
  // UI language → server error messages come back in the same language (also signed out).
  const lang = localStorage.getItem("yalola.lang") || (typeof navigator !== "undefined" ? navigator.language : "");
  if (lang) config.headers["x-lang"] = String(lang).slice(0, 2).toLowerCase();
  // Réseau instable : une lecture ne doit jamais rester suspendue indéfiniment (les envois de fichiers gardent leur propre délai).
  if (String(config.method || "get").toLowerCase() === "get" && config.timeout == null) config.timeout = guardOn("net_resilience") ? adaptiveTimeout(30000) : 30000;
  // Lot 628 — clé d'idempotence : publier / story / commenter reçoivent un client_id ; si la réponse se perd (réseau lent)
  // et que l'envoi repart avec le MÊME objet (file d'envoi, « Réessayer »), le serveur renvoie l'existant au lieu d'un doublon.
  if (isSocialAction(config) && !/\/follow$/.test(String(config.url || "")) && config.data && typeof config.data === "object"
      && !(typeof FormData !== "undefined" && config.data instanceof FormData) && !config.data.client_id) {
    config.data.client_id = newClientId();
  }
  return config;
});

const newClientId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

// iOS : la 1re action « sociale » réussie (publier, story, commenter, suivre) — celles qui rapportent des notifications —
// est le contexte naturel de la demande de permission notifications (lib/nativePush.requestNativePushPermission).
const SOCIAL_ACTION = /^\/?(posts|stories|posts\/[^/]+\/comments|users\/[^/]+\/follow)$/;
export const isSocialAction = (cfg) =>
  String(cfg?.method || "").toLowerCase() === "post" && SOCIAL_ACTION.test(String(cfg?.url || "").split("?")[0]);

api.interceptors.response.use(
  (r) => {
    try { window.dispatchEvent(new Event("yalola:server-ok")); } catch { /* noop */ }
    if (isSocialAction(r?.config)) import("@/lib/nativePush").then((m) => m.requestNativePushPermission()).catch(() => {});
    return r;
  },
  (err) => {
    // Lot 713 — serveur sans réponse sur une lecture → nouvel essai (2 max) avant de laisser l'erreur remonter.
    if (guardOn("net_resilience") && shouldRetryRead(err, err?.config)) {
      const cfg = err.config;
      cfg.__retries = (cfg.__retries || 0) + 1;
      return new Promise((r) => setTimeout(r, retryDelay(cfg.__retries - 1))).then(() => api.request(cfg));
    }
    // Origin/CDN momentarily down during a redeploy (Cloudflare 520-526 or
    // 502/503/504) → signal a friendly "app is updating" overlay instead of
    // leaking the raw Cloudflare error page. Non-invasive: we still re-throw so
    // every existing error handler keeps working.
    const status = err?.response?.status;
    if (status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 526)) {
      try { window.dispatchEvent(new Event("yalola:server-unavailable")); } catch { /* noop */ }
    }
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      err.friendlyMessage = detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
    } else if (typeof detail === "string") {
      err.friendlyMessage = detail;
    } else if (detail && typeof detail === "object" && detail.code === "profile_incomplete") {
      // Lot 580 : inscription allégée — profil incomplet → la feuille « Complète ton profil » s'ouvre (ProfileCompletionGate).
      err.friendlyMessage = detail.message || "Complète ton profil pour interagir.";
      err.profileIncomplete = true;
      try { window.dispatchEvent(new CustomEvent("yalola:profile_incomplete", { detail })); } catch { /* noop */ }
    } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
      err.friendlyMessage = detail.message;
    }
    return Promise.reject(err);
  }
);

export const asError = (e) =>
  e?.friendlyMessage || e?.response?.data?.detail || e?.message || "Une erreur est survenue";
